import { useEffect, useState } from "react";
import { Button, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCheckboxBlankOutline, mdiCheckboxMarkedOutline, mdiCheckCircle, mdiChevronDown, mdiChevronRight } from "@mdi/js";

import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { CriteriaRecomendationEntity } from "src/entities/CriteriaRecomendationEntity";

export type RecomendationSelections = Record<number, CriteriaRecomendationEntity[]>;

type RecomendationsStepProps = {
	manifestations: CriteriaManifestationEntity[];
	recomendations: CriteriaRecomendationEntity[];
	selections: RecomendationSelections;
	onChange: (selections: RecomendationSelections) => void;
	onCanAdvanceChange: (canAdvance: boolean) => void;
};

export default function RecomendationsStep({ manifestations, recomendations, selections, onChange, onCanAdvanceChange }: RecomendationsStepProps) {
	const [expandedManifestations, setExpandedManifestations] = useState<Set<number>>(() => new Set(manifestations[0] ? [manifestations[0].id] : []));

	useEffect(() => {
		setExpandedManifestations(new Set(manifestations[0] ? [manifestations[0].id] : []));
	}, [manifestations]);

	useEffect(() => {
		onCanAdvanceChange(true);
	}, [onCanAdvanceChange]);

	function handleToggleManifestation(manifestationId: number) {
		setExpandedManifestations((current) => {
			const next = new Set(current);
			if (next.has(manifestationId)) {
				next.delete(manifestationId);
			} else {
				next.add(manifestationId);
			}
			return next;
		});
	}

	function handleToggle(manifestationId: number, recomendation: CriteriaRecomendationEntity) {
		const manifestationSelections = selections[manifestationId] ?? [];
		const checked = manifestationSelections.some((selectedRecomendation) => selectedRecomendation.id === recomendation.id);

		onChange({
			...selections,
			[manifestationId]: checked
				? manifestationSelections.filter((selectedRecomendation) => selectedRecomendation.id !== recomendation.id)
				: [...manifestationSelections, recomendation],
		});
	}

	return (
		<div className="d-flex flex-column gap-2 w-100">
			{manifestations.map((manifestation) => {
				const expanded = expandedManifestations.has(manifestation.id);
				const manifestationRecomendations = recomendations.filter((recomendation) =>
					recomendation.manifestations.some((relatedManifestation) => relatedManifestation.id === manifestation.id)
				);

				return (
					<div key={manifestation.id}>
						<Button
							type="button"
							variant="light"
							className="d-flex align-items-center justify-content-between rounded-3 border px-3 fw-medium w-100 text-start"
							style={{ minHeight: 40 }}
							onClick={() => handleToggleManifestation(manifestation.id)}
							aria-expanded={expanded}
						>
							<span>{manifestation.title}</span>
							<span className="d-flex align-items-center gap-2">
								<OverlayTrigger overlay={<Tooltip>Manifestação concluída</Tooltip>}>
									<Icon path={mdiCheckCircle} size={0.85} color="#198754" />
								</OverlayTrigger>
								<Icon path={expanded ? mdiChevronDown : mdiChevronRight} size={0.8} />
							</span>
						</Button>

						<Collapse in={expanded}>
							<div>
								<div className="d-flex flex-column gap-2 my-3 ms-4">
									{manifestationRecomendations.length === 0 ? (
										<div className="small text-muted">Nenhuma recomendação vinculada a esta manifestação.</div>
									) : (
										manifestationRecomendations.map((recomendation) => {
											const checked = (selections[manifestation.id] ?? []).some(
												(selectedRecomendation) => selectedRecomendation.id === recomendation.id
											);

											return (
												<Button
													key={recomendation.id}
													type="button"
													variant="outline-dark"
													className={`d-flex align-items-center w-100 gap-3 rounded-3 border text-start ${checked ? "active" : ""}`}
													onClick={() => handleToggle(manifestation.id, recomendation)}
												>
													<div>
														<Icon path={checked ? mdiCheckboxMarkedOutline : mdiCheckboxBlankOutline} size={1} />
													</div>
													{recomendation.title}
												</Button>
											);
										})
									)}
								</div>
							</div>
						</Collapse>
					</div>
				);
			})}
		</div>
	);
}
